import { deleteCookie, setCookie } from 'cookies-next'
import { OptionsType } from 'cookies-next/lib/types'
import { NextRequest, NextResponse } from 'next/server'
import {
  ANGEL_COOKIE_DOMAIN,
  ANGEL_PREVIEW_COOKIE_DOMAIN,
  AUTH_JWT_COOKIE,
  AUTH_SESSION_COOKIE,
} from '@/constants/cookies'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { isDevelopmentEnvironment, isProductionEnvironment } from '@/utils/environment-utils'
import { decodeAccessToken, DecodedJwt, isDecodedJwt } from '../auth-utils'
import { Maybe } from '../types'

export type CookieRequestType = OptionsType['req']
export type CookieResponseType = OptionsType['res']

/** Cookie options with path guaranteed to be a string (always provided by getCookieOptions) */
export type CookieOptionsWithPath = Omit<OptionsType, 'path' | 'secure' | 'sameSite'> & {
  path: string
  secure: boolean
  sameSite: boolean | 'lax' | 'strict' | 'none'
}

/**
 * Gets the cookie domain for the current environment.
 * Production: .angel.com
 * Preview: .angel-preview.com
 * Other (staging, localhost): undefined (host-only cookies)
 */
export const getCookieDomain = (): string | undefined => {
  if (isProductionEnvironment()) {
    return ANGEL_COOKIE_DOMAIN
  }

  try {
    const baseUrl = paths.getBaseURL()
    const url = new URL(baseUrl)
    if (url.hostname === 'angel-preview.com' || url.hostname.endsWith('.angel-preview.com')) {
      return ANGEL_PREVIEW_COOKIE_DOMAIN
    }
  } catch {
    // If URL parsing fails, fall back to undefined
  }

  // Staging, localhost, and other environments - no domain (host-only cookies)
  return undefined
}

/**
 * Returns cookie domain options object for spreading into cookie options.
 */
export const getCookieOptions = (customOptions: Partial<OptionsType> = {}): CookieOptionsWithPath => {
  const options: CookieOptionsWithPath = {
    ...customOptions,
    sameSite: customOptions.sameSite ?? 'lax',
    path: customOptions.path ?? '/',
    secure: customOptions.secure ?? !isDevelopmentEnvironment(),
  }

  const domain = getCookieDomain()
  if (domain && !options.domain) {
    options.domain = domain
  }

  return options
}

/** Legacy auth cookie names that should be cleaned up for all users. */
const LEGACY_AUTH_COOKIES = [
  // v1 cookies
  'angel_jwt',
  'angelSession',
  'angelSession.0',
  'angelSession.1',
  'angelSession.2',
  'angelSession.3',
  'angelSession.4',
  // v2 cookies (chunked session format)
  'angelSession_v2',
  'angelSession_v2.0',
  'angelSession_v2.1',
  'angelSession_v2.2',
  'angelSession_v2.3',
  'angelSession_v2.4',
]

/**
 * Deletes legacy v1 auth cookies via the middleware response.
 * Deletes both with and without domain to ensure removal regardless of original scope.
 * Only sets deletion headers for cookies that are actually present on the request.
 */
export function deleteLegacyAuthCookies(request: NextRequest, response: NextResponse<unknown>) {
  const domain = getCookieDomain()

  LEGACY_AUTH_COOKIES.forEach((name) => {
    if (request.cookies.has(name)) {
      response.cookies.delete({
        name,
        path: '/',
      })

      if (domain) {
        response.cookies.delete({
          name,
          domain,
          path: '/',
        })
      }
    }
  })
}

export function deleteAuthCookies(customOptions: Partial<OptionsType> = {}) {
  const domainOptions = getCookieOptions(customOptions)
  deleteCookie(AUTH_SESSION_COOKIE, domainOptions)
  deleteCookie(AUTH_JWT_COOKIE, domainOptions)
}

export function setJwtCookie(
  authToken: Maybe<string>,
  customOptions: Partial<OptionsType> = {},
  expireBefore: number = 30,
) {
  const decodedToken = decodeAccessToken(authToken)
  if (!authToken || !decodedToken) return

  const maxAge = calculateJwtCookieMaxAge(decodedToken, expireBefore)
  const domainOptions = getCookieOptions({ maxAge, ...customOptions })
  setCookie(AUTH_JWT_COOKIE, authToken, domainOptions)
}

export function calculateJwtCookieMaxAge(
  authToken: Maybe<string> | Maybe<DecodedJwt>,
  expireBefore: number = 30,
): number {
  const fallbackMaxAge = Durations.ONE_HOUR_IN_SECONDS

  const decodedToken = isDecodedJwt(authToken) ? authToken : decodeAccessToken(authToken)
  if (!authToken || !decodedToken) return fallbackMaxAge

  const expireCookieInSecondsBeforeTokenExpires = expireBefore
  const iat = typeof decodedToken?.iat === 'number' ? decodedToken.iat : undefined
  const exp = typeof decodedToken?.exp === 'number' ? decodedToken.exp : undefined
  const computed = !!exp && !!iat && exp > iat ? exp - iat - expireCookieInSecondsBeforeTokenExpires : undefined
  const maxAge = !!computed && computed > 0 ? computed : fallbackMaxAge

  return maxAge
}
